<script setup>
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, reactive, ref, watch } from 'vue';

const props = defineProps({
    validMsg: {
        type: Object,
        default: () => ({})
    },
    rentType: {
        type: String,
        default: 'SHORT'
    },
    vehicleGroupId: {
        type: Number,
        default: 0
    },
    detailData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update']);
const collisionUnit = ref('');
const initialData = {
    collision_insurance_age: '',
    collision_insurance_state: 'NON',
    collision_coverage_type: '',
    collision_insurance_coverage: ''
};
const data = reactive({ ...initialData });

const initialValidMessage = {
    collision_insurance_age: '',
    collision_insurance_state: '',
    collision_coverage_type: '',
    collision_insurance_coverage: ''
};
const validationMessage = reactive({ ...initialValidMessage });
const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
    emit('update', structuredClone(initialData));
};

const getData = () => {
    return {
        collision_insurance_age: data.collision_insurance_age,
        collision_insurance_state: data.collision_insurance_state,
        collision_coverage_type: data.collision_coverage_type,
        collision_insurance_coverage: data.collision_insurance_coverage
    };
};

const updateInputValue = (inputId, val) => {
    console.log('들어온값 ', val);
    console.log('값의 id ', inputId);

    switch (inputId) {
        case 'col_insurance':
            data.collision_insurance_age = Number(val.collision_insurance_age);
            data.collision_insurance_state = val.collision_insurance_state;
            data.collision_coverage_type = val.collision_coverage_type;
            data.collision_insurance_coverage = val.collision_insurance_coverage;
            validationMessage.collision_insurance_state = '';
            validationMessage.collision_insurance_age = '';
            validationMessage.collision_coverage_type = '';
            validationMessage.collision_insurance_coverage = '';
    }
};

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || Object.keys(newVal).length === 0) return;

        if (typeof newVal !== 'object') {
            return;
        }

        Object.keys(newVal).forEach((key) => {
            if (key.includes('contract_data.')) {
                const pureKey = key.replace('contract_data.', '');
                if (pureKey in validationMessage) {
                    validationMessage[pureKey] = newVal[key];
                }
            } else {
                if (key in validationMessage) {
                    validationMessage[key] = newVal[key];
                }
            }
        });
    },
    { immediate: true }
);

watch(
    data,
    () => {
        emit('update', { ...data });
    },
    { deep: true }
);

watch(
    () => data.collision_insurance_state,
    (newVal, oldVal) => {
        if (newVal === 'NON' || newVal === '') {
            nextTick(() => {
                data.collision_insurance_state = 'NON';
                data.collision_coverage_type = '';
                data.collision_insurance_coverage = '';
                data.collision_insurance_age = '';
                validationMessage.collision_insurance_state = '';
                validationMessage.collision_coverage_type = '';
                validationMessage.collision_insurance_coverage = '';
                validationMessage.collision_insurance_age = '';
            });
        }
    },
    { immediate: true }
);

watch(
    () => data.collision_coverage_type,
    (newVal) => {
        console.log(newVal);
        if (newVal === '') {
            data.collision_insurance_coverage = '';
            validationMessage.collision_insurance_coverage = '';
            collisionUnit.value = '';
        } else {
            collisionUnit.value = newVal === 'R' ? '%' : '원';
        }
    },
    { immediate: true }
);

// watch(
//     () => props.vehicleGroupId,
//     (newVal) => {
//         console.log('차량그룹 들어왔는가', props.vehicleGroupId);
//     }
// );

watch(
    () => props.rentType,
    (newVal, oldVal) => {
        if (newVal === 'SHORT' && newVal !== oldVal) {
            resetData();
        }
    }
);
const isCollisionReadOnly = computed(() => data.collision_insurance_state === 'NON' || data.collision_insurance_state === '');
const isCoverageReadOnly = computed(() => isCollisionReadOnly.value || data.collision_coverage_type === '');
const rentTypeReadOnly = computed(() => props.rentType === 'SHORT');
const isAgeReadOnly = computed(() => data.collision_insurance_state === 'NON' || data.collision_insurance_state === '' || rentTypeReadOnly.value);
//const collisionUnit = computed(() => (data.collision_coverage_type === 'R' ? '%' : '원'));

const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const setDetailData = (newVal) => {
    Object.assign(data, newVal);

    console.log('들어온 자차보험 정보!!!', data);
};

const clearCollisionInfo = () => {
    resetData();
};

defineExpose({
    getData,
    resetData,
    setDetailData
});
</script>
<template>
    <div class="section-box base-text">
        <div class="section-body">
            <div class="section-left">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <h2 class="section-title mb-2 z-[-10]">자차 보험 정보</h2>
                        <button class="mb-1" title="초기화 (선택안함)" @click="clearCollisionInfo()">
                            <i class="pi pi-undo text-[gray]"></i>
                        </button>
                    </div>

                    <div class="flex justify-end">
                        <colinsurance
                            inputId="col_insurance"
                            :vehicleGroupId="vehicleGroupId"
                            :rentType="rentType"
                            :labelEmpty="true"
                            :justifyContent="'start'"
                            @input="(val) => updateInputValue('col_insurance', val)"
                            :width="'8.5rem'"
                            :height="'2rem'"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="collision_insurance_state"
                            :placeholder="'자차 종류를 선택하세요.'"
                            :options="commonCode('COLLISION_INSURANCE_STATE').value"
                            v-model="data.collision_insurance_state"
                            :errmsg="validationMessage.collision_insurance_state"
                            :padding="'0.5rem'"
                            :readonly="rentTypeReadOnly"
                            label="자차 종류"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="collision_insurance_age"
                            :placeholder="'보험 가입 연령을 선택하세요.'"
                            :options="commonCode('INSURANCE_AGE').value"
                            v-model="data.collision_insurance_age"
                            :errmsg="validationMessage.collision_insurance_age"
                            :padding="'0.5rem'"
                            :readonly="isAgeReadOnly"
                            label="보험 가입 연령"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="collision_coverage_type"
                            :placeholder="'자차 한도 종류를 선택하세요.'"
                            :options="commonCode('COLLISION_COVERAGE_TYPE').value"
                            v-model="data.collision_coverage_type"
                            :errmsg="validationMessage.collision_coverage_type"
                            :padding="'0.5rem'"
                            :readonly="isCollisionReadOnly"
                            label="자차 한도"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="collision_insurance_coverage"
                            :number="'n'"
                            :placeholder="'자차 한도를 입력하세요.'"
                            :unit="collisionUnit"
                            v-model="data.collision_insurance_coverage"
                            :errmsg="validationMessage.collision_insurance_coverage"
                            :readonly="isCoverageReadOnly"
                            :padding="'0.5rem 2.5rem 0.5rem 0.5rem'"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
<style scoped>
.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    transition: color 0.3s ease;
    border-radius: 0.4rem;
    background-color: #f0f0ff;
    color: #3868ff;
    align-items: center;
    /* position: absolute; */
    display: flex;
    justify-content: center;
    border: 1px solid #3868ff;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}
</style>
